// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import refreshProduct from "@pacific/App/Handlers/ProductDetails/RefreshProduct";
import { updateCustomerProductNumber } from "@pacific/App/Handlers/ProductDetails/UpdateCustomerProductNumber";
import xProductPartNumbersStyles from "@pacific/App/SharedStyle/ProductPartNumbersStyles";
import { xIsAdminstrator } from "@pacific/App/Store/Selectors/GeneralSelectors";
import Edit from "@pacific/XDL/Icons/Edit";
import { iconLinkCommonStyles, mergeWithCss } from "@pacific/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

// XNG-Changes
const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    isAdminstrator: xIsAdminstrator(state),
    search: getLocation(state).search,
});

const mapDispatchToProps = {
    updateCustomerProductNumber,
    refreshProduct,
    loadCurrentCart,
};

type Props = WidgetProps & HasProduct & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsCustomerPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    changeCpnLink?: LinkPresentationProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
}

export const customerPartNumberStyles: ProductDetailsCustomerPartNumberStyles = {
    labelText: {
        ...mergeWithCss(xProductPartNumbersStyles.label, {
            css: css`
                margin-right: 8px;
            `,
        }),
    },
    valueText: {
        ...mergeWithCss(xProductPartNumbersStyles.value, {
            css: css``,
        }),
    },
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            & .actions-container {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-top: 16px;
                width: 100%;
                & > button:first-child {
                    margin-right: 20px;
                }
            }
        `,
    },
    changeCpnLink: {
        ...mergeWithCss(iconLinkCommonStyles(Edit), {
            css: css`
                ${IconWrapper} {
                    opacity: 1;
                }
            `,
        }),
    },
    cancelButton: {
        css: css`
            margin-right: auto;
        `,
    },
    deleteButton: {
        css: css`
            margin-left: auto;
            margin-right: 12px;
        `,
    },
    submitButton: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        size: 12,
        css: css`
            margin-left: 8px;
        `,
    },
};

const styles = customerPartNumberStyles;

const ProductDetailsCustomerPartNumber: React.FC<Props> = ({
    cart,
    product,
    isAdminstrator,
    search,
    refreshProduct,
    updateCustomerProductNumber,
    loadCurrentCart,
}) => {
    if (!isAdminstrator && !product.customerProductNumber) {
        return null;
    }
    // XNG-Changes
    const toaster = useToaster();
    const [showEditModal, setShowEditModal] = useState(false);
    const [customerProductNumber, setCustomerProductNumber] = useState({
        isLoading: false,
        editValue: product.customerProductNumber,
        result: product.customerProductNumber,
    });

    const queryParams = parseQueryString<{ option?: string; criteria?: string }>(search.replace("?", ""));
    const styledOption = (queryParams.option?.toString() || queryParams.criteria?.toString() || "").toLocaleLowerCase();

    useEffect(() => {
        const productIsInCart = cart?.cartLines?.find(cartLine => {
            return cartLine.productId === product.id;
        });

        if (productIsInCart) {
            loadCurrentCart();
        }
    }, [customerProductNumber.result]);

    const handleCloseModal = () => {
        if (customerProductNumber.isLoading) {
            return;
        }
        setShowEditModal(false);
    };

    const handleChangeCustomerProductNumber: React.ChangeEventHandler<HTMLInputElement> = e => {
        if (customerProductNumber.isLoading) {
            return;
        }
        setCustomerProductNumber(prevState => ({
            ...prevState,
            editValue: e.target.value,
        }));
    };

    const handleResetCustomerProductNumber = () => {
        if (customerProductNumber.isLoading) {
            return;
        }
        setCustomerProductNumber(prevState => ({
            isLoading: false,
            editValue: prevState.result,
            result: prevState.result,
        }));
        handleCloseModal();
    };

    const handleSubmitCustomerProductNumber = () => {
        if (customerProductNumber.isLoading) {
            return;
        }
        setCustomerProductNumber(prevState => ({
            ...prevState,
            isLoading: true,
        }));

        updateCustomerProductNumber({
            customerPartNumber: customerProductNumber.editValue,
            productId: product.id,
            onSuccess: result => {
                setCustomerProductNumber(prevState => ({
                    isLoading: false,
                    editValue: prevState.editValue,
                    result: prevState.editValue,
                }));
                toaster.addToast({
                    body: customerProductNumber.result
                        ? translate("The Customer Product Number Updated!")
                        : translate("The Customer Product Number Added!"),
                    messageType: "success",
                });
                refreshProduct({
                    path: product.canonicalUrl,
                    styledOption,
                    getProductLines: true,
                    addToRecentlyViewed: true,
                });
                handleCloseModal();
            },
            onError: result => {
                setCustomerProductNumber(prevState => ({
                    ...prevState,
                    isLoading: false,
                }));
                toaster.addToast({
                    body: result,
                    messageType: "danger",
                });
                handleCloseModal();
            },
        });
    };

    // XNG-Changes
    const handleDeleteCustomerProductNumber = () => {
        if (customerProductNumber.isLoading) {
            return;
        }
        setCustomerProductNumber(prevState => ({
            ...prevState,
            isLoading: true,
        }));

        updateCustomerProductNumber({
            customerPartNumber: "",
            productId: product.id,
            onSuccess: result => {
                setCustomerProductNumber(prevState => ({
                    isLoading: false,
                    editValue: "",
                    result: "",
                }));
                toaster.addToast({
                    body: translate("The Customer Product Number deleted!"),
                    messageType: "success",
                });
                refreshProduct({
                    path: product.canonicalUrl,
                    styledOption,
                    getProductLines: true,
                    addToRecentlyViewed: true,
                });
                handleCloseModal();
            },
            onError: result => {
                setCustomerProductNumber(prevState => ({
                    ...prevState,
                    isLoading: false,
                }));
                toaster.addToast({
                    body: result,
                    messageType: "danger",
                });
                handleCloseModal();
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="pdp-customerPartNumber">
            <Typography {...styles.valueText} as="p" data-test-selector="customerProductNumber">
                <Typography {...styles.labelText}>{translate("Your Part #")}</Typography>
                {customerProductNumber.result}
            </Typography>
            {/* <XNG-Changes> */}
            {isAdminstrator && customerProductNumber.result && (
                <Link
                    {...styles.changeCpnLink}
                    onClick={() => setShowEditModal(true)}
                    aria-label="Change customer product number"
                />
            )}
            {isAdminstrator && !customerProductNumber.result && (
                <Link
                    {...styles.changeCpnLink}
                    onClick={() => setShowEditModal(true)}
                    aria-label="Add customer product number"
                >
                    {translate("Add Customer Part Number")}
                </Link>
            )}
            <Modal
                isOpen={showEditModal || customerProductNumber.isLoading}
                sizeVariant="medium"
                handleClose={handleCloseModal}
                closeOnEsc={!customerProductNumber.isLoading}
                headline={
                    product.customerProductNumber ? translate("Edit Customer Part #") : translate("Add Customer Part #")
                }
            >
                <StyledWrapper {...styles.formWrapper}>
                    <TextField
                        label={translate("Customer Part #")}
                        value={customerProductNumber.editValue}
                        onChange={handleChangeCustomerProductNumber}
                        disabled={customerProductNumber.isLoading}
                    />
                    <StyledWrapper className="actions-container">
                        <Button
                            variant="tertiary"
                            sizeVariant="medium"
                            onClick={handleResetCustomerProductNumber}
                            disabled={customerProductNumber.isLoading}
                            {...styles.cancelButton}
                            aria-label="Cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        {/* XNG-Changes */}
                        {customerProductNumber.result && (
                            <Button
                                variant="tertiary"
                                sizeVariant="medium"
                                onClick={handleDeleteCustomerProductNumber}
                                disabled={customerProductNumber.isLoading}
                                {...styles.deleteButton}
                                aria-label="Delete"
                            >
                                {translate("Delete")}
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            sizeVariant="medium"
                            onClick={handleSubmitCustomerProductNumber}
                            disabled={customerProductNumber.editValue === "" || customerProductNumber.isLoading}
                            {...styles.submitButton}
                            aria-label="Submit"
                        >
                            {translate("Submit")}
                            {customerProductNumber.isLoading && <LoadingSpinner {...styles.spinner} />}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
            {/* </XNG-Changes> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsCustomerPartNumber)),
    definition: {
        displayName: "Customer Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
